/**
 * Parser Dispatcher: Routes to v1 or v2 parser based on schema version
 * This allows us to support both old (v1) and new optimized (v2) schemas
 */

import { parseDiagramData as parseV1, validateDiagramData as validateV1, ParseResult } from './parser-v1'
import { parseDiagramData as parseV2, validateDiagramData as validateV2 } from './parser-v2'
import type { DiagramData } from '../types/schema'

export type { ParseResult } from './parser-v1'

/**
 * Main entry point: Automatically detects schema version and routes to correct parser
 */
export function parseDiagramData(data: DiagramData): ParseResult {
  const version = detectSchemaVersion(data)

  if (version === 'v2') {
    return parseV2(data)
  }

  // Default to v1 (backward compatible)
  return parseV1(data)
}

/**
 * Validate diagram data using the appropriate validator
 */
export function validateDiagramData(data: any): { valid: boolean; errors: string[] } {
  const version = detectSchemaVersion(data)

  if (version === 'v2') {
    return validateV2(data)
  }

  return validateV1(data)
}

/**
 * Detect schema version from diagram data
 * v2 schemas have one or more of these fields: nodeTemplates, edgePatterns, nodeReplicas, schemaVersion
 * Everything else defaults to v1 for safety
 */
function detectSchemaVersion(data: any): 'v1' | 'v2' {
  // Explicit version field takes precedence
  if (data.schemaVersion === 'v2') {
    return 'v2'
  }

  // Auto-detect v2 features
  if (
    data.nodeTemplates ||
    data.edgePatterns ||
    data.nodeReplicas ||
    data.nodesFromTemplate
  ) {
    return 'v2'
  }

  // Default to v1 (safe fallback for all existing diagrams)
  return 'v1'
}
